import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/redux/reducers/rootReducer';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { isObjectEmpty } from '../../../../common/src/legacy/services/objectService';
import OffersToolbarContainer from '../offers/OffersToolbarContainer';
import ToolbarProgressBarStep from './ToolbarProgressBarStep';
import { getRouteMatch } from '../../services/productFactoryService';
import {
  isRiskAnalysisFlow,
  useFlowsSelector,
} from '../../../../common/src/utils/globalVariables';
import { FlowsQuestionnaire } from '../../../../flows/src/types/FlowsTypes';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';
import { getTagContentFromStore } from '../../../../flows/src/components/ui/common/hooks/useTagContent';

interface Match {
  position?: string | undefined;
  sessionId?: string | undefined;
}

const ToolbarProgressBar: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const questionnaire = useFlowsSelector((state) => state.flows.questionnaire);
  const visitedTags = useFlowsSelector(
    (state) => state.flows.metaData.visitedTags,
  );
  const {
    selectedRiskObjects,
    selectedGuarantees,
    selectedCompanies,
    companies,
    guarantees,
  } = useSelector((state: RootState) => state.selection);

  const sessionId = useSessionId();

  const [match, setMatch] = useState<Match>();

  const getLastPositions = (questionnaire: FlowsQuestionnaire) =>
    Object.assign(
      {},
      ...questionnaire.groups.map(({ tag }, index: number) => ({
        [tag]: index,
      })),
    );

  const [lastPositions, setLastPositions] = useState<{ [x: string]: number }>(
    getLastPositions(questionnaire),
  );

  useEffect(() => {
    if (location.pathname) setMatch(getRouteMatch(location.pathname));
  }, [location]);

  useEffect(() => {
    setLastPositions(getLastPositions(questionnaire));
  }, [questionnaire]);

  const position =
    typeof match?.position === 'string' ? parseInt(match?.position) : -1;

  const getIsCompleted = (tag: string) => {
    // If tag was visited and is not the last one, it's completed
    if (
      visitedTags.includes(tag) &&
      visitedTags[visitedTags.length - 1] !== tag
    ) {
      return true;
      // If on finalize route, all is done
    }
    return location.pathname.includes('/product/session/finalize');
  };

  // SELECTION Toolbar
  if (location.pathname.match(new RegExp('/product/session/selection.*')))
    return (
      <StepContainer data-test-id="ProdFac_ToolbarProgressBar">
        <ToolbarProgressBarStep
          isCompleted={selectedRiskObjects.length > 0}
          onClick={() => history.push('0')}
          inProgress={position === 0}
        >
          {t('Choose risk objects')}
        </ToolbarProgressBarStep>

        {!isSocialLoginUrl && (
          <>
            <ToolbarProgressBarStep
              isCompleted={selectedCompanies.length > 0}
              disabled={
                selectedRiskObjects.length === 0 || companies.length === 0
              }
              onClick={() =>
                selectedRiskObjects.length > 0 &&
                companies.length > 0 &&
                history.push('1')
              }
              inProgress={position === 1}
            >
              {t('Choose companies')}
            </ToolbarProgressBarStep>
            <ToolbarProgressBarStep
              isCompleted={
                Object.entries(selectedGuarantees)
                  .filter(([key]) => selectedRiskObjects.includes(key))
                  .every(([_, val]) => val.length > 0) &&
                !isObjectEmpty(selectedGuarantees) &&
                selectedRiskObjects.length > 0
              }
              disabled={
                selectedCompanies.length === 0 ||
                Object.keys(guarantees).length === 0
              }
              onClick={() =>
                selectedCompanies.length > 0 &&
                Object.keys(guarantees).length > 0 &&
                history.push('2')
              }
              inProgress={position > 1}
            >
              {t('Choose guarantees')}
            </ToolbarProgressBarStep>
          </>
        )}
      </StepContainer>
    );

  // Questions Toolbar
  if (
    location.pathname.match(
      new RegExp('/product/session/(questions|finalize)/.*'),
    ) ||
    location.pathname.match(
      new RegExp('/risk-analysis/(questions|finalize)/.*'),
    )
  )
    return (
      <StepContainer data-test-id="ProdFac_ToolbarProgressBar">
        {Object.keys(lastPositions).map((key, index) => {
          const isCompleted = getIsCompleted(key);
          const tagPosition =
            index === 0 ? 0 : lastPositions[visitedTags[index - 1]] + 1;
          return (
            <ToolbarProgressBarStep
              key={`progress_${key}`}
              isCompleted={isCompleted}
              disabled={
                !visitedTags.includes(key) ||
                location.pathname.includes('/product/session/finalize')
              }
              onClick={() =>
                history.push(
                  `${
                    isRiskAnalysisFlow() ? '/risk-analysis' : '/product/session'
                  }/questions/${sessionId || '0'}/${tagPosition}`,
                )
              }
              inProgress={key === questionnaire.groups[position]?.tag}
            >
              {getTagContentFromStore(key) || t(`FLOWS_TOOLBAR_${key}`)}
            </ToolbarProgressBarStep>
          );
        })}
      </StepContainer>
    );

  // Offers Toolbar
  if (location.pathname.match(new RegExp('/product/session/(offers).*')))
    return <OffersToolbarContainer />;

  return null;
};

const StepContainer = styled.div`
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  z-index: 2;
  grid-gap: 0.8rem;
`;

export default ToolbarProgressBar;
